import { ForwardedRef, forwardRef, useEffect, useRef, useState } from 'react';
import Card from '../Card';
import { colors } from '../../../../colors';
import { useEditAreNotificationsEnabledMutation } from '../../../../store/services/api';
import Checkbox from '../../../../components/Checkbox';

interface Props {
  isActive: boolean;
  userId: number | null;
  moveToStepFour: () => void;
}

const InfoOnboarding = forwardRef(
  (
    { isActive, userId, moveToStepFour }: Props,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const [editAreNotificationsEnabled] =
      useEditAreNotificationsEnabledMutation();
    const [areNotificationsEnabled, setAreNotificationsEnabled] =
      useState(true);

    const handleAreNotificationsEnabledChange = (value: boolean) => {
      editAreNotificationsEnabled({ userId, areNotificationsEnabled: value });
      setAreNotificationsEnabled(value);
    };

    const scrollableRef = useRef<HTMLDivElement>(null);
    const [showOverlay, setShowOverlay] = useState(false);

    useEffect(() => {
      const handleScroll = () => {
        if (scrollableRef.current) {
          const { scrollTop, scrollHeight, clientHeight } =
            scrollableRef.current;
          // Show overlay if not at the bottom (more content below)
          const isMoreContentBelow = scrollTop + clientHeight < scrollHeight;
          setShowOverlay(isMoreContentBelow);
        }
      };

      const scrollableElement = scrollableRef.current;
      if (scrollableElement) {
        scrollableElement.addEventListener('scroll', handleScroll);
        // Initial check on mount
        handleScroll();
      }

      return () => {
        if (scrollableElement) {
          scrollableElement.removeEventListener('scroll', handleScroll);
        }
      };
    }, []);

    return (
      <Card index={3} title="Useful Information" isActive={isActive} ref={ref}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
            height: '100%',
          }}
        >
          <div
            ref={scrollableRef}
            style={{
              gap: 24,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              position: 'relative',
            }}
            className="no-scrollbar"
          >
            <div>
              <p
                style={{
                  margin: 0,
                  color: colors.white,
                  fontSize: 12,
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                }}
              >
                Email Notification
              </p>
              <Checkbox
                label={'I want to receive emails from transactions'}
                value={areNotificationsEnabled}
                handleValueChange={handleAreNotificationsEnabledChange}
                color={colors.orange}
              />
            </div>

            <div>
              <p
                style={{
                  margin: 0,
                  color: colors.mossGreen,
                  fontSize: 12,
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                }}
              >
                Credits system
              </p>
              <p
                style={{
                  margin: 0,
                  color: colors.white,
                  fontSize: 16,
                  fontWeight: 'bold',
                }}
              >
                Initially, you will receive 50 credits. You use these credits to
                subscribe to a bot. In normal circumstances, subscribing to a
                bot will require 1 credit. In case a new bot needs to be
                created, this will require 2 credit. When multiple users
                subscribe to the same bot, you will get half your credits back.
                The maximum amount of subscriptions you can have active at the
                same time is between 25 and 50, depending on how populair the
                bot is.
              </p>
            </div>

            <div>
              <p
                style={{
                  margin: 0,
                  color: colors.mossGreen,
                  fontSize: 12,
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                }}
              >
                Exchange connections
              </p>
              <p
                style={{
                  margin: 0,
                  color: colors.white,
                  fontSize: 16,
                  fontWeight: 'bold',
                }}
              >
                For the bots to be able to work properly, they require limited
                access to your exchange account. Meaning they are able to see
                your balance and perform basic actions like buying and selling.
                Bots are never able to withdraw your balance.
              </p>
            </div>

            <div>
              <p
                style={{
                  margin: 0,
                  color: colors.mossGreen,
                  fontSize: 12,
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                }}
              >
                Pausing subscriptions
              </p>
              <p
                style={{
                  margin: 0,
                  color: colors.white,
                  fontSize: 16,
                  fontWeight: 'bold',
                }}
              >
                At all times, you are able to pause a subscription. This means
                that you will not be included when making transactions, your
                balance will not be touched. Be aware that when you pause a
                subscription when the bot has bought, you will be vulnerable to
                market movement, meaning if the market goes down, so will your
                balance.
              </p>
            </div>
          </div>

          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'end',
              flex: '0 0 auto',
            }}
          >
            <button
              onClick={moveToStepFour}
              style={{
                backgroundColor: colors.green,
                border: `1px solid ${colors.green}`,
                borderRadius: 4,
                cursor: 'pointer',
                padding: 6,
                paddingLeft: 12,
                paddingRight: 12,
                color: colors.white,
                fontWeight: 'bold',
                textWrap: 'nowrap',
                fontSize: 16,
              }}
            >
              Continue
            </button>
          </div>

          {showOverlay && (
            <div
              style={{
                position: 'absolute',
                bottom: 70 /* Above the button (adjust based on button height) */,
                left: 0,
                right: 0,
                height: 100 /* Height of the fade effect */,
                background:
                  'linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.9))' /* Gradient fade */,
                pointerEvents: 'none' /* Allow clicks through overlay */,
              }}
            ></div>
          )}
        </div>
      </Card>
    );
  },
);

InfoOnboarding.displayName = 'InfoOnboarding';

export default InfoOnboarding;

/** Ask if the user wants to receive emails
 * Give quick info about credits system, pausing subscriptions, available pages
 */
